/**
 * App.tsx — Top-level shell (task B10; game navigation in G04; dashboard
 * in H01).
 *
 * On mount it refreshes the authenticated state via GET /api/v1/auth/session
 * (which also rotates the CSRF token and refreshes it in memory). When
 * authenticated, the dashboard (H01) is shown; "Open Game" navigates to the
 * GamePage (G04) which subscribes to the game room and refetches
 * authoritative state on every change.
 */
import { useCallback, useEffect, useState } from 'react';
import type { UserDto } from '@shared';
import { apiV1, csrfToken } from './api';
import { AuthScreen } from './screens/AuthScreen';
import { DashboardScreen } from './screens/DashboardScreen';
import { GamePage } from './screens/GamePage';
import { SnowBackground } from './SnowBackground';

export function App() {
  const [user, setUser] = useState<UserDto | null>(null);
  const [checkingSession, setCheckingSession] = useState(true);
  // The currently open game (H04: same page for creator and player).
  const [openGameId, setOpenGameId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiV1
      .session()
      .then((data) => {
        if (!cancelled) setUser(data.user);
      })
      .catch(() => {
        if (!cancelled) setUser(null);
      })
      .finally(() => {
        if (!cancelled) setCheckingSession(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleAuthenticated = useCallback((nextUser: UserDto) => {
    setUser(nextUser);
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await apiV1.logout();
    } finally {
      csrfToken.set(null);
      setUser(null);
      setOpenGameId(null);
    }
  }, []);

  if (checkingSession) {
    return (
      <>
        <SnowBackground />
        <main className="app-shell">
          <p>Loading…</p>
        </main>
      </>
    );
  }

  if (!user) {
    return (
      <>
        <SnowBackground />
        <AuthScreen onAuthenticated={handleAuthenticated} />
      </>
    );
  }

  if (openGameId !== null) {
    return (
      <>
        <SnowBackground />
        {/* H04: the SAME page for creator and player; controls come from the
            backend permissions inside the fetched game state. */}
        <GamePage
          key={openGameId}
          gameId={openGameId}
          userId={user.id}
          onBack={() => setOpenGameId(null)}
        />
      </>
    );
  }

  return (
    <>
      <SnowBackground />
      <DashboardScreen user={user} onOpenGame={setOpenGameId} onLogout={() => void handleLogout()} />
    </>
  );
}
